import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileJson, ShieldCheck, Trash2 } from "lucide-react";
import { api, type PrivacyRequest } from "../api/client";

export function PrivacyPage() {
  const queryClient = useQueryClient();
  const [lastResult, setLastResult] = useState<Record<string, unknown> | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const requests = useQuery({ queryKey: ["privacy-requests"], queryFn: () => api<PrivacyRequest[]>("/api/admin/privacy-requests") });

  const createRequest = useMutation({
    mutationFn: (body: unknown) => api<PrivacyRequest>("/api/admin/privacy-requests", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["privacy-requests"] })
  });

  const completeRequest = useMutation({
    mutationFn: (id: string) => api<{ request: PrivacyRequest; result: Record<string, unknown> }>(`/api/admin/privacy-requests/${id}/complete`, {
      method: "POST",
      body: JSON.stringify({ currentPassword })
    }),
    onSuccess: (response) => {
      setLastResult(response.result);
      queryClient.invalidateQueries({ queryKey: ["privacy-requests"] });
    }
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    createRequest.mutate({
      type: String(form.get("type")),
      requesterReference: String(form.get("requesterReference")),
      notes: String(form.get("notes") || "")
    });
    event.currentTarget.reset();
  }

  return (
    <div className="page-stack">
      <section className="tool-hero card privacy-hero">
        <div>
          <span className="panel-eyebrow left">Privacy Ops</span>
          <h2>DSAR workflow za export, deletion i rectification.</h2>
          <p>Reference se porede sa submission ID, unit/sobom, emailom ili telefonom; svaka akcija ulazi u audit log.</p>
        </div>
        <div className="object-brand-card">
          <ShieldCheck size={34} />
          <strong>{requests.data?.filter((request) => request.status !== "completed").length ?? 0} otvoreno</strong>
          <span>GDPR request queue</span>
        </div>
      </section>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Novi zahtjev</h2>
              <p>Requester reference moze biti submission ID, soba/unit, email ili telefon.</p>
            </div>
          </div>
          <form className="compact-form" onSubmit={submit}>
            <select name="type" defaultValue="export">
              <option value="export">Export</option>
              <option value="delete">Delete/anonymize</option>
              <option value="rectify">Rectify note</option>
            </select>
            <input name="requesterReference" placeholder="Reference, npr. soba 302 ili email" required />
            <textarea name="notes" rows={4} placeholder="Interna napomena, bez nepotrebnog PII" />
            <button type="submit"><FileJson size={16} /> Kreiraj zahtjev</button>
          </form>
          {lastResult && (
            <pre className="result-box">{JSON.stringify(lastResult, null, 2)}</pre>
          )}
        </section>

        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Zahtjevi</h2>
              <p>Export i delete traze potvrdu trenutne lozinke prije completion-a.</p>
            </div>
          </div>
          <label className="compact-form">
            Trenutna lozinka za sensitive akcije
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="Obavezno za export/delete"
            />
          </label>
          <div className="privacy-list">
            {(requests.data ?? []).map((request) => (
              <article className="privacy-card" key={request.id}>
                <div>
                  <strong>{request.type} - {request.status}</strong>
                  <span>{request.requesterReference}</span>
                  <small>{new Date(request.createdAt).toLocaleString()}</small>
                </div>
                <div className="row-actions">
                  <button
                    disabled={request.status === "completed" || (["export", "delete"].includes(request.type) && !currentPassword)}
                    onClick={() => completeRequest.mutate(request.id)}
                  >
                    {request.type === "delete" ? <Trash2 size={16} /> : <ShieldCheck size={16} />} Complete
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
